import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  parseHexColor,
  POWER_SLIDE_SWATCHES,
} from "../../../models/interactions/PowerSlideTheme";
import PowerSlidePopover from "./PowerSlidePopover";

interface PowerSlideColorFieldProps {
  label: string;
  value: string;
  ariaLabel: string;
  onChange: (color: string) => void;
  /**
   * Colours that would leave text unreadable are filtered out rather than
   * offered and then refused, so the author only ever sees what works.
   */
  isUsable?: (color: string) => boolean;
}

/**
 * A colour row: a wide bar showing the current colour, opening a popover of
 * curated swatches with a hex field behind them. Curated first is deliberate -
 * the audience is HR and L&D staff, so the common case is picking, not typing.
 */
export default function PowerSlideColorField(props: PowerSlideColorFieldProps) {
  const { label, value, ariaLabel, onChange, isUsable } = props;
  const usable = (color: string) => isUsable?.(color) ?? true;
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [draft, setDraft] = useState(value);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      setDraft(value);
      setShowCustom(false);
    }
  }, [isOpen, value]);

  const draftHex = draft.startsWith("#") ? draft : `#${draft}`;
  const draftIsValid = Boolean(parseHexColor(draftHex)) && usable(draftHex);

  const commitDraft = () => {
    if (!draftIsValid) return;
    onChange(draftHex);
    setIsOpen(false);
  };

  return (
    <div className="flex w-full flex-col gap-[6px]">
      <span className="text-[13px] font-medium text-creator-text-sub">
        {label}
      </span>
      <button
        type="button"
        ref={triggerRef}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-[8px] rounded-[7px] border border-solid border-creator-border py-[6px] pl-[6px] pr-[9px] hover:border-creator-primary"
      >
        <span
          className="h-[20px] flex-1 rounded-[6px] border border-solid"
          style={{ backgroundColor: value, borderColor: "rgba(39,45,55,0.14)" }}
        />
        <KeyboardArrowDownIcon
          sx={{ fontSize: 14 }}
          className="text-creator-text-sub"
        />
      </button>

      {/* Portalled: the properties column scrolls and each section is
          overflow-hidden, so an in-flow popover is clipped immediately. */}
      <PowerSlidePopover
        anchorRef={triggerRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        ariaLabel={`${ariaLabel}-picker`}
      >
        <div className="flex flex-col gap-[10px] px-[12px] pb-[10px] pt-[12px]">
          {POWER_SLIDE_SWATCHES.map((row, rowIndex) => (
            <div key={rowIndex} className="flex gap-[6px]">
              {row.filter(usable).map((swatch) => (
                <button
                  key={swatch}
                  type="button"
                  aria-label={`${ariaLabel}-swatch-${swatch}`}
                  aria-pressed={swatch.toLowerCase() === value.toLowerCase()}
                  onClick={() => {
                    onChange(swatch);
                    setIsOpen(false);
                  }}
                  className={classNames(
                    "h-[24px] w-[24px] rounded-[6px] border-solid",
                    swatch.toLowerCase() === value.toLowerCase()
                      ? "border-2 border-creator-primary"
                      : "border"
                  )}
                  style={{
                    backgroundColor: swatch,
                    ...(swatch.toLowerCase() !== value.toLowerCase() && {
                      borderColor: "rgba(39,45,55,0.14)",
                    }),
                  }}
                />
              ))}
            </div>
          ))}

          <div className="h-px w-full bg-creator-border" />

          {showCustom ? (
            <div className="flex items-center gap-[6px]">
              <input
                aria-label={`${ariaLabel}-hex`}
                value={draft}
                autoFocus
                spellCheck={false}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") commitDraft();
                }}
                className="w-[92px] rounded-[6px] border border-solid border-creator-border bg-creator-surface px-[8px] py-[4px] text-[12.5px] text-creator-text outline-none focus:border-creator-primary"
              />
              <button
                type="button"
                aria-label={`${ariaLabel}-hex-apply`}
                disabled={!draftIsValid}
                onClick={commitDraft}
                className="rounded-[6px] border border-solid border-creator-primary px-[10px] py-[4px] text-[12.5px] font-semibold text-creator-primary disabled:opacity-40"
              >
                {t("powerslide.theme.apply", { defaultValue: "Apply" })}
              </button>
            </div>
          ) : (
            <button
              type="button"
              aria-label={`${ariaLabel}-custom`}
              onClick={() => setShowCustom(true)}
              className="text-left text-[12.5px] font-semibold text-creator-primary"
            >
              {t("powerslide.theme.custom", { defaultValue: "Custom colour…" })}
            </button>
          )}
        </div>
      </PowerSlidePopover>
    </div>
  );
}
